import { useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ConfigProvider, Layout } from "antd";

import { PrivateRoute } from '@qualitas/auth';
import Navbar, { NavbarApi, useNavbar, Footer } from "@qualitas/navbar";

import Stepper from '../FormContract/Stepper/Stepper';
import Overview from "../Overview/Overview";
import Administration from "../Administration/Administration";

import { PlacementAgentsAPI } from "../../Api/PlacementAgentsAPI";

import { IAppLayout } from "./IAppLayout";
import { PlacementAgent, PlacementAgentValues } from "../../Models";
import ExternalContract from "../ExternalContract/ExternalContract";
import HomePage from "../HomePage/HomePage";
import LanguageSelector from "../HomePage/LanguageSelector";

const AppLayout = ({
	baseURL,
	baseURLExternal,
	clientId,
	validateAuthToken,
	language,
	setLanguage,
	logout,
	userId,
	userContactId,
	userEmail,
	userLogo,
	userPrimaryColor,
	userIdInstitution,
	sendResetPasswordEmail,
	translations,
	availableLanguages,
	translationsConfiguration,
	saveTranslations,
	createTranslationLanguage,
	translationsLoading,
	isInvestor,
	moduleRole
}: IAppLayout) => {
	const navigate = useNavigate();
	let location = useLocation();

	const [placementAgents, setPlacementAgents] = useState<PlacementAgent[]>([]);
	const [placementAgentActiveVehicles, setPlacementAgentActiveVehicles] = useState<PlacementAgentValues[]>([]);
	const [placementAgentAllVehicles, setPlacementAgentAllVehicles] = useState<PlacementAgentValues[]>([]);
	const [externalContractInvestors, setExternalContractInvestors] = useState<any[]>([]);

	const normalizePlacementAgents = (agents: any[]): PlacementAgent[] => (
		(Array.isArray(agents) ? agents : []).map((agent: any) => ({
			idInstitution: Number(agent?.idInstitution ?? agent?.IdInstitution ?? 0),
			logo: String(agent?.logo ?? agent?.Logo ?? ""),
			institutionLong: String(agent?.institutionLong ?? agent?.InstitutionLong ?? ""),
			institutionValues: (agent?.institutionValues ?? agent?.InstitutionValues ?? []).map((value: any) => ({
				confirmed: Number(value?.confirmed ?? value?.Confirmed ?? 0),
				reserved: Number(value?.reserved ?? value?.Reserved ?? 0),
				notConfirmed: Number(value?.notConfirmed ?? value?.NotConfirmed ?? 0),
				total: Number(value?.total ?? value?.Total ?? 0),
				ticket: Number(value?.ticket ?? value?.Ticket ?? 0),
				available: Number(value?.available ?? value?.Available ?? 0),
				maxAvailable: Number(value?.maxAvailable ?? value?.MaxAvailable ?? 0),
				vehicleLegalName: String(value?.vehicleLegalName ?? value?.VehicleLegalName ?? ""),
				vehicleId: Number(value?.vehicleId ?? value?.VehicleId ?? 0),
				vehicleShortName: String(value?.vehicleShortName ?? value?.VehicleShortName ?? ""),
				active: Boolean(value?.active ?? value?.Active ?? false)
			}))
		}))
	);
	
	const api = NavbarApi(baseURLExternal, clientId);
	const { translations: navbarTranslations, ...NavbarProps} = useNavbar({
		api,
		idSystemMaster: 1,
		userData: {
			userId,
			userContactId,
			userEmail,
			userLogo,
			userPrimaryColor
		},
		navigate,
		migratedUrl: process.env['REACT_APP_APP_URL'],
    	notMigratedUrl: process.env['REACT_APP_APP_URL'],
		sendResetPasswordEmail: () => sendResetPasswordEmail(userEmail),
		language
	});

	const placementAgentsAPI = PlacementAgentsAPI(baseURL, baseURLExternal, clientId);

 	useEffect(() => {
		if(isInvestor) {
			if(externalContractInvestors?.length === 0){
				placementAgentsAPI.getExternalContractInvestors()
					.then((res: any) => {
						if(res.success){
							setExternalContractInvestors(res.data);
						}
					});
			}
			return;
		}; 

		placementAgentsAPI.getInstitutions()
			.then((res: any) => {
				if(res.success){
					const normalizedPlacementAgents = normalizePlacementAgents(res.data);
					setPlacementAgents(normalizedPlacementAgents);
					let inst = normalizedPlacementAgents.filter((x: PlacementAgent) => x.idInstitution === userIdInstitution);
					
					if(inst.length > 0) {
						const activeVehicles = inst[0].institutionValues.filter((x: PlacementAgentValues) => x.active) || [];
						setPlacementAgentActiveVehicles(activeVehicles);
						setPlacementAgentAllVehicles(inst[0].institutionValues || []);
						if(userIdInstitution !== 536){
							setPlacementAgents(normalizedPlacementAgents.filter((x: PlacementAgent) => x.idInstitution === userIdInstitution));
						}
						
					}
				}
			});
	}, [userIdInstitution, isInvestor]);
 
	ConfigProvider.config({
		theme:{
			primaryColor: '#b3d615',
			infoColor: '#b3d615',
			successColor: '#8fac10'
		}
	});

	return (
		<Layout>
			{(location.pathname !== '/') && 
				<Navbar
					{...NavbarProps}
					userName={NavbarProps.menuName ?? ""}
					logout={logout}
					reportIncident={() => console.log("report")}
					isBackoffice={false}
					translations={navbarTranslations}
					extraContent={
						<LanguageSelector
							setLanguage={setLanguage}
							language={language}
							availableLanguages={availableLanguages}
						/>
					}
				/>
			}
			<ConfigProvider>
				<Routes>
					<Route path="/" element={<PrivateRoute validateAuthToken={validateAuthToken}/>}>
						<Route path="/" element={
							<HomePage
								menuItems={NavbarProps.menuItems} 
								logout={logout} 
								language={language} 
								setLanguage={setLanguage} 
								translations={translations}
								availableLanguages={availableLanguages}
								userLogo={userLogo}
								userPrimaryColor={userPrimaryColor}
								isInvestor={isInvestor}
								externalContractInvestors={externalContractInvestors}
								moduleRole={moduleRole}
							/>
						} />
						<Route path="/Overview" element={
							<Overview
								baseURL={baseURL}
								baseURLExternal={baseURLExternal}
								clientId={clientId}
								translations={translations}
								userEmail={userEmail}
								userId={userId}
								userIdInstitution={userIdInstitution}
								placementAgents={placementAgents}
								language={language}
								moduleRole={moduleRole}
							/>
						} />
						<Route path="/FormContract" element={
							<Stepper
								baseURL={baseURL}
								baseURLExternal={baseURLExternal}
								translations={translations}
								userIdInstitution={userIdInstitution}
								clientId={clientId}
								placementAgentVehicles={placementAgentActiveVehicles}
								userEmail={userEmail}
								language={language}
								isInvestor={isInvestor}
								moduleRole={moduleRole}
							/>
						}/>
						<Route path="/TransferContract" element={
							<Stepper
								baseURL={baseURL}
								baseURLExternal={baseURLExternal}
								translations={translations}
								userIdInstitution={userIdInstitution}
								clientId={clientId}
								placementAgentVehicles={placementAgentAllVehicles}
								userEmail={userEmail}
								language={language}
								isInvestor={isInvestor}
								moduleRole={moduleRole}
							/>
						}/>
						<Route path="/ExternalContract" element={
							<ExternalContract
								baseURL={baseURL}
								baseURLExternal={baseURLExternal}
								clientId={clientId}
								translations={translations}
								userIdInstitution={userIdInstitution}
							/>
						}/>
						<Route path="/Administration" element={
							<Administration
								baseURL={baseURL}
								baseURLExternal={baseURLExternal}
								clientId={clientId}
								translations={translations}
								translationsConfiguration={translationsConfiguration}
								saveTranslations={saveTranslations}
								createTranslationLanguage={createTranslationLanguage}
								translationsLoading={translationsLoading}
								moduleRole={moduleRole}
							/>
						}/>
					</Route>
					<Route path="*" element={<h1>Redirección</h1>}/>
				</Routes>
			</ConfigProvider>

			<Footer language={language}/>
		</Layout>
	);
};

export default AppLayout;
